import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { exportToXLS } from 'utils/export/export';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Table from 'components/CustomTable/CustomTable';
import TableHeader from 'components/TableHeader/TableHeader';
import { DepartmentsCell, LinkCell } from 'components/Cells/Cells';
import CustomFilter, { FilterType } from 'components/CustomFilter/CustomFilter';
import Phone from '@material-ui/icons/PhoneOutlined';
import Account from '@material-ui/icons/AccountCircleOutlined';
import { translate } from 'components/Translation';
import { AccountsService } from 'services/accounts/accounts';
import { DepartmentsService } from 'services/departments/departments';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch } from 'react-redux';
import { throwError } from 'rxjs';
import { finalize } from 'rxjs/operators';
import styles from 'assets/jss/material-dashboard-pro-react/views/payments/billingAccounts/numbersStyle';
import { makeStyles } from '@material-ui/core';
import { HeadingMBold } from 'components/Typography/Typography';
import moment from 'moment';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { searchItemsInColumns } from 'utils/searchTable/searchTable';
import { SingleBillingNumber } from 'utils/api/models/accounts/singleBillingNumber';
import useParseFixFbbNumbers from 'commons/hooks/numbers';
import { numberDetailsPath } from 'utils/paths/numberDetailsPath';
import { AAMALI_PLUS_ACCOUNT_CATEGORY } from 'views/Numbers/constants';
import { TagManager } from 'commons/gtm/tagManager';
import { getLanguage } from 'utils/language/language';

const useStyles = makeStyles(styles);

interface INumbersProps {
	match: {
		path: string;
		params: {
			subpage: string;
			billingId: string;
			accountId: string;
		};
	};
}

export interface IExtendedSingleNumber extends SingleBillingNumber {
	department?: SingleDepartment;
}

enum UserType {
	ASSIGNED = 'ASSIGNED',
	NOT_ASSIGNED = 'NOT_ASSIGNED',
}

const Numbers = (props: INumbersProps) => {
	const { billingId, accountId } = props.match.params;
	const [loadingNumbers, setLoadingNumbers] = useState(false);
	const [loadingDepartments, setLoadingDepartments] = useState(true);
	const [tempNumbers, setTempNumbers] = useState<Array<SingleBillingNumber>>([]);
	const parsedNumbers = useParseFixFbbNumbers(tempNumbers) as IExtendedSingleNumber[];
	const [departments, setDepartments] = useState<Array<SingleDepartment>>([]);
	const [filteredNumbers, setFilteredNumbers] = useState<Array<IExtendedSingleNumber>>([]);
	const [searchInputValue, setSearchInputValue] = useState('');
	const [selectedNumbers, setSelectedNumbers] = useState<Array<number>>([]);
	const [allNumbers, setNumbers] = useState<any>([]);
	const [filters, setFilters] = useState<{
		department: string[];
		username: string[];
	}>({
		department: [],
		username: [],
	});
	const dispatch = useDispatch();
	const classes = useStyles();
	const numbers = useMemo(() => {
		return parsedNumbers.map((number) => ({
			...number,
			department: departments.find((department) => department.deptId === number.deptId),
		})
		);
	}, [parsedNumbers, departments, allNumbers]);
	const getNumberDetailsPath = (number: IExtendedSingleNumber) => {
		return numberDetailsPath(
			number.accountNum,
			billingId,
			number.accountType,
			number.billingType,
			number.upAccountId,
			number.accountCategory === AAMALI_PLUS_ACCOUNT_CATEGORY,
			AAMALI_PLUS_ACCOUNT_CATEGORY,
		);
	};

	const getUserDetailsPath = (user: IExtendedSingleNumber) => {
		return `/dashboard/users/${user.userId}/user-details`;
	};

	useEffect(() => {
		dispatch(showLoadingIndicator());
		DepartmentsService.getDepartmentsForCurrentUser()
			.pipe(finalize(() => setLoadingDepartments(false)))
			.subscribe(
				({ departments }) => {
					setDepartments(
						(departments || []).filter(
							(department) => !!department.deptId && department.accountId === accountId
						)
					);
				},
				(error) => throwError(error)
			);
		return () => {
			dispatch(hideLoadingIndicator());
		};
	}, [dispatch, accountId]);

	useEffect(() => {
		if (accountId && billingId) {
			setLoadingNumbers(true);
			dispatch(showLoadingIndicator());
			AccountsService.getNumbersForBillingAccount(accountId, billingId)
				.pipe(finalize(() => setLoadingNumbers(false)))
				.subscribe(
					({ numbers }) => {
						if (numbers) {
							setTempNumbers(numbers);
						}
					},
					(error) => throwError(error)
				);
		}
		return () => {
			dispatch(hideLoadingIndicator());
		};
	}, [accountId, dispatch, billingId, departments]);

	useEffect(() => {
		if (!loadingDepartments && !loadingNumbers) {
			dispatch(hideLoadingIndicator());
		}
	}, [loadingDepartments, loadingNumbers, dispatch]);

	useEffect(() => {
		setFilteredNumbers(() => {
			let data = [...numbers];

			if (filters['department'].length > 0) {
				data = data.filter(({ deptId }) => !!deptId && filters['department'].indexOf(deptId) >= 0);
			}

			if (filters['username'].length > 0) {
				const assignedActive = filters['username'].indexOf(UserType.ASSIGNED) >= 0;
				const notAssignedActive = filters['username'].indexOf(UserType.NOT_ASSIGNED) >= 0;
				data = data.filter(({ username }) => {
					const isAssigned = assignedActive && !!username;
					const isNotAssigned = notAssignedActive && !username;
					return isAssigned || isNotAssigned;
				});
			}

			if (searchInputValue !== '') {
				data = searchItemsInColumns(data, ['accountNum', 'username'], searchInputValue);
			}

			return data;
		});
	}, [numbers, searchInputValue, filters]);

	const exportToXls = useCallback(() => {
		const source =
			selectedNumbers.length === 0 ? filteredNumbers : selectedNumbers.map((index) => filteredNumbers[index]);
		const preparedData = source.map((number) => ({
			Number: number.accountNum,
			'User name': number.username ? number.username : translate('username.not.assigned'),
			Department: number.department ? number.department.name : translate('department.not.assigned'),
		}));

		exportToXLS(preparedData, `numbers ${moment().format('LLL')}`);
	}, [filteredNumbers, selectedNumbers]);

	const filterChanged = useCallback(
		(name, value) => {
			setFilters((filters) => ({ ...filters, [name]: value }));
		},
		[setFilters]
	);

	// calling this API that fetches accountCategory and this accountCategory is used to show Aamali+services
	useEffect(() => {
		dispatch(showLoadingIndicator());
		AccountsService.getCorpUserNumbersWithChilds()
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
				})
			)
			.subscribe(
				({ accounts: numbersArr }) => {
					if (numbersArr) {
						numbersArr = Array.isArray(numbersArr) ? numbersArr : [numbersArr];
						setNumbers(numbersArr)
					}
				},
				(error) => throwError(error)
			);
		return () => {
			dispatch(hideLoadingIndicator());
		};
	},[accountId, dispatch, billingId, departments]);

	useEffect(()=>{
		TagManager.dataLayer({
			dataLayer: {
				event: 'My Services',
				step: 'My Active Services:Numbers',
				accountID: accountId,
				billingID: billingId,
				language: getLanguage()
			},
		});
	}, [])

	return (
		<GridContainer justify="flex-start">
			<BreadcrumbsItem to={`/dashboard/active-services/${billingId}/numbers`}>
				{`${billingId} - ${translate('path.numbers')}`}
			</BreadcrumbsItem>
			<GridItem xs={12}>
				<div className={classes.header}>
					<HeadingMBold color="secondary-light" disableMargin>
						{translate('billing.account.numbers.list')}
					</HeadingMBold>
				</div>
			</GridItem>
			<GridItem xs={12}>
				<CustomFilter
					onChange={filterChanged}
					value={filters}
					filters={[
						{
							title: translate('numbers.department'),
							name: 'department',
							type: 'multiple' as FilterType,
							elements: departments.map((department) => ({
								title: department.name,
								value: String(department.deptId),
								color: department.uiColor,
							})),
						},
						{
							title: translate('users'),
							name: 'username',
							type: 'one' as FilterType,
							elements: [
								{
									title: translate('assigned'),
									value: UserType.ASSIGNED,
								},
								{
									title: translate('username.not.assigned'),
									value: UserType.NOT_ASSIGNED,
								},
							],
						},
					]}
				/>
			</GridItem>
			<GridItem xs={12}>
				<TableHeader
					setSearchInputValue={(value) => setSearchInputValue(value)}
					searchInputValue={searchInputValue}
					exportToXLS={exportToXls}
				/>
				<Table
					withHeader
					total={filteredNumbers.length}
					withCheckbox
					onSelect={(value) => setSelectedNumbers(value)}
					selected={selectedNumbers}
					selectPanel={{
						selectedElements: selectedNumbers.length,
						downloadMenuItems: [
							{
								name: 'XSLX',
								onClick: exportToXls,
							},
						],
					}}
					tableProps={{
						data: filteredNumbers,
						loading: loadingNumbers || loadingDepartments,
						defaultPageSize: 10,
						columns: [
							{
								Header: translate('phoneNumber'),
								accessor: 'accountNum',
								Cell: ({ original }: any) => {
									return (
										<LinkCell
											text={original.accountNum}
											path={getNumberDetailsPath(original)}
											icon={<Phone />}
											/**	added to show aamali+services, in case of micro bundle when we go from Numbers tab to Number details
										accountCategory is used as an identifier for microbundle */
											props={{ accountCategory: original.accountCategory }}
										/>
									);
								},
								width: 150,
							},
							{
								Header: translate('user.name'),
								accessor: 'username',
								width: 200,
								Cell: ({ original }: any) => (
									<LinkCell
										text={original.username}
										path={getUserDetailsPath(original)}
										icon={<Account />}
										/**	added to show aamali+services, in case of micro bundle when we go from Numbers tab to Number details
										accountCategory is used as an identifier for microbundle */
										props={{ accountCategory: original.accountCategory }}
									/>
								),
							},
							{
								Header: translate('department'),
								accessor: 'departmentId',
								Cell: ({ original }: any) => (
									<DepartmentsCell departments={original.department ? [original.department] : []} />
								),
							},
						],
					}}
				/>
			</GridItem>
		</GridContainer>
	);
};

export default Numbers;
